class SearchForm extends HTMLElement {

  static MIN_SEARCH_CHARS = SEARCH_MIN_CHARS;

  get gender() {
    return this.querySelector('input[name="tags"]:checked')?.value
  }
  
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.searchForm = this.querySelector('form');
    this.predictiveSearch = this.querySelector('[data-predictive-search]');
    this.popularSearchesList = this.querySelector('[data-popular-searches]');
    this.recentSearchesList = this.querySelector('[data-recent-searches]');
    this.fallbackSearchesList = this.querySelector('[data-fallback-searches]');
    this.algoliaUtils = new AlgoliaUtils();
    this.isSearchActive = false;
    this.emptyStateRendered = false;
    this.lastPublishedKey = null;
  }


  connectedCallback() {
    this.setDefaultGender();
    this.setListeners();
  }

  setListeners() {
    this.input.form.addEventListener('reset', this.onFormReset.bind(this));

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.debouncedSave = debounce(() => {
      this.saveToLastSearches();
    }, 3000);

    this.searchForm.addEventListener('input', (event) => {
      if (event.target.type !== 'search') return;

      this.debouncedOnChange(event);
      this.debouncedSave();
    });

    this.searchForm.addEventListener('change', (event) => {
      if (event.target.type === 'search') return;
      this.updateRecommendedSwipers();
      this.onChange();
    });

    this.searchForm.addEventListener('submit', (event) => {
      event.preventDefault();
    });

    this.input.addEventListener('focus', () => {
      this.activateSearch();
    });

    const details = this.closest('search-details-modal')?.querySelector('details');
    details?.addEventListener('toggle', () => {
      if (!details.open) {
        this.isSearchActive = false;
        this.emptyStateRendered = false;
        this.lastPublishedKey = null;
        return;
      }
      this.activateSearch();
    });

    subscribe(PUB_SUB_EVENTS.predictiveSearchEmpty, () => {
      this.handleEmptySearch();
    });
  }

  activateSearch() {
    this.isSearchActive = true;
    this.updateRecommendedSwipers();
    this.onChange();
  }

  setHeight() {
    const formHeight = this.offsetHeight;
    const facetsHeight = this.querySelector('search-filters-form')?.offsetHeight || 0;

    const height = formHeight + facetsHeight + 'px';
    this.style.setProperty('--grid-height', height);
  }

  setDefaultGender() {
    const gender = localStorage.getItem('active_gender');
    if (!gender) return;

    this.searchForm.querySelectorAll('input[name="tags"]').forEach((input) => {  
        input.checked = input.value == gender;   
    });
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  saveToLastSearches() {
    const value = this.input?.value?.trim();
    if (value.length < SearchForm.MIN_SEARCH_CHARS) return;

    let lastSearches = [];
    try {
      const stored = localStorage.getItem('lastSearches');
      if (stored) lastSearches = JSON.parse(stored);
    } catch (e) {
      lastSearches = [];
    }

    lastSearches = lastSearches.filter((item) => item !== value);
    lastSearches.push(value);
    const maxItems = 10;
    if (lastSearches.length > maxItems) {
      lastSearches = lastSearches.slice(-maxItems);
    }

    try {
      localStorage.setItem('lastSearches', JSON.stringify(lastSearches));
    } catch (e) {}
  }

  onChange() {
    const value = this.input.value.trim();
    const isActive = value.length >= SearchForm.MIN_SEARCH_CHARS;

    this.predictiveSearch?.toggleAttribute('data-active', isActive);

    if (isActive) {
      this.emptyStateRendered = false;
      this.setHeight();
      this.publishSearchUpdate(value);
      return;
    }

    this.lastPublishedKey = null;

    if (!this.isSearchActive || value) return;

    if (this.emptyStateRendered) {
      this.updateRecommendedSwipers();
      return;
    }

    this.clearFields();
    this.renderEmptySearch();
  }

  /**
   * Asks the results grid for a new search, skipping terms already requested
   * for the current gender to avoid duplicated Algolia queries.
   *
   * @param {string} value - Trimmed search term.
   * @returns {void}
   */
  publishSearchUpdate(value) {
    const gender = this.gender;
    const key = `${value}|${gender || ''}`;

    if (key === this.lastPublishedKey) return;
    this.lastPublishedKey = key;

    publish(PUB_SUB_EVENTS.predictiveSearchUpdate, { value, gender });
  }

  updateRecommendedSwipers() {
    this.querySelectorAll('[data-recommended-search-gender] swiper-slider').forEach((slider) => {
      if (slider.offsetParent === null) return;

      slider.swiper?.update();
      slider.scheduleLayoutFix?.();
    });
  }

  /**
   * Renders the empty state of the overlay: recent and popular searches.
   * The popular list does not depend on the typed term nor on the gender, so it
   * is requested once per overlay opening.
   *
   * @returns {Promise<void>}
   */
  async renderEmptySearch() {
    this.emptyStateRendered = true;
    this.handleRecentSearches();

    try {
      this.handleSuggestions(await this.algoliaUtils.getSuggestions());
    } catch {
      this.emptyStateRendered = false;
    }

    this.updateRecommendedSwipers();
  }

  createBtn(text) {
    const li = document.createElement('li');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tw-body-font';
    btn.setAttribute('data-search-btn', '');
    btn.textContent = text;
    btn.addEventListener('click', () => this.applySuggestion(text));
    li.appendChild(btn);
    return li;
  }

  async handleSuggestions(suggestions) {
    (suggestions?.hits ?? []).forEach((item) => {
      const text = typeof item === 'string' ? item : (item?.objectID ?? '');
      if (!text) return;
      this.popularSearchesList.appendChild(this.createBtn(text, 'popular-search'));
    });
  }

  async handleFallbackSearches(suggestions) {
    const query = this.input.value.trim().toLowerCase();

    (suggestions?.hits ?? []).forEach((item) => {
      const text = typeof item === 'string' ? item : (item?.objectID ?? '');
      if (!text) return;
      if (query && text.toLowerCase().split(/\s+/).some((word) => word.includes(query))) return;
      this.fallbackSearchesList.appendChild(this.createBtn(text));
    });
  }

  handleRecentSearches() {
    let lastSearches = [];
    const stored = localStorage.getItem('lastSearches');
    if (stored) lastSearches = JSON.parse(stored);
    lastSearches.forEach((text) => {
      this.recentSearchesList.appendChild(this.createBtn(text));
    });
  }

  applySuggestion(text) {
    if (!this.input) return;
    this.input.value = text;
    this.input.focus();
    this.activateSearch();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    event.preventDefault();
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.lastPublishedKey = null;
      this.toggleResetButton();
    }
  }

  /**
   * Renders alternative searches when the typed term returns no product.
   *
   * @returns {Promise<void>}
   */
  async handleEmptySearch() {
    if (!this.isSearchActive) return;

    this.clearFields();
    this.emptyStateRendered = false;
    this.predictiveSearch?.toggleAttribute('data-active', false);

    const suggestions = await this.algoliaUtils.getSuggestions(this.input.value);
    this.handleFallbackSearches(suggestions);
    this.updateRecommendedSwipers();
  }

  clearFields() {
    this.popularSearchesList.innerHTML = '';
    this.recentSearchesList.innerHTML = '';
    this.fallbackSearchesList.innerHTML = '';
  }
}

customElements.define('search-form', SearchForm);
